import React, { useState } from 'react';
import { ethers } from 'ethers';
import { BESTCRYPTOTWITS_TOKEN, STAKING_CONFIG } from './config';
import { switchToBaseNetwork } from './utils/network';
import './App.css';

const ERC20_ABI = [
  "function balanceOf(address) view returns (uint256)",
  "function approve(address spender, uint256 amount) returns (bool)",
  "function allowance(address owner, address spender) view returns (uint256)"
];

const STAKING_ABI = [
  "function stake(uint256 amount)",
  "function unstake(uint256 amount)",
  "function claimRewards()",
  "function getStake(address user) view returns (uint256 amount, uint256 timestamp, uint256 pendingRewards, uint256 effectiveRate, uint256 nextClaimTime)",
  "function getContractStats() view returns (uint256 totalStaked, uint256 rewardPool, uint256 currentRewardRate, uint256 contractBalance)"
];

function App() {
  const [account, setAccount] = useState('');
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [tokenBalance, setTokenBalance] = useState('0');
  const [stakedAmount, setStakedAmount] = useState('0');
  const [pendingRewards, setPendingRewards] = useState('0');
  const [stakeInput, setStakeInput] = useState('');
  const [unstakeInput, setUnstakeInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const connectWallet = async () => {
    try {
      if (!window.ethereum) {
        alert('Please install MetaMask!');
        return;
      }

      await switchToBaseNetwork();

      const web3Provider = new ethers.providers.Web3Provider(window.ethereum);
      const accounts = await web3Provider.send("eth_requestAccounts", []);
      const web3Signer = web3Provider.getSigner();

      setProvider(web3Provider);
      setSigner(web3Signer);
      setAccount(accounts[0]);

      await loadData(web3Provider, accounts[0]);
    } catch (error) {
      console.error('Error connecting wallet:', error);
      alert('Failed to connect wallet');
    }
  };

  const loadData = async (web3Provider, userAccount) => {
    try {
      const tokenContract = new ethers.Contract(
        BESTCRYPTOTWITS_TOKEN.address,
        ERC20_ABI,
        web3Provider
      );

      const stakingContract = new ethers.Contract(
        STAKING_CONFIG.contractAddress,
        STAKING_ABI,
        web3Provider
      );

      const balance = await tokenContract.balanceOf(userAccount);
      setTokenBalance(ethers.utils.formatEther(balance));

      const stakeInfo = await stakingContract.getStake(userAccount);
      setStakedAmount(ethers.utils.formatEther(stakeInfo.amount));
      setPendingRewards(ethers.utils.formatEther(stakeInfo.pendingRewards));
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleStake = async () => {
    if (!stakeInput || parseFloat(stakeInput) <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    setLoading(true);
    try {
      const tokenContract = new ethers.Contract(
        BESTCRYPTOTWITS_TOKEN.address,
        ERC20_ABI,
        signer
      );

      const stakingContract = new ethers.Contract(
        STAKING_CONFIG.contractAddress,
        STAKING_ABI,
        signer
      );

      const amount = ethers.utils.parseEther(stakeInput);

      const allowance = await tokenContract.allowance(
        account,
        STAKING_CONFIG.contractAddress
      );

      if (allowance.lt(amount)) {
        const approveTx = await tokenContract.approve(
          STAKING_CONFIG.contractAddress,
          ethers.constants.MaxUint256
        );
        await approveTx.wait();
      }

      const stakeTx = await stakingContract.stake(amount);
      await stakeTx.wait();

      alert('Staked successfully!');
      setStakeInput('');
      await loadData(provider, account);
    } catch (error) {
      console.error('Error staking:', error);
      alert('Failed to stake: ' + (error?.message || error));
    }
    setLoading(false);
  };

  const handleUnstake = async () => {
    if (!unstakeInput || parseFloat(unstakeInput) <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    setLoading(true);
    try {
      const stakingContract = new ethers.Contract(
        STAKING_CONFIG.contractAddress,
        STAKING_ABI,
        signer
      );

      const amount = ethers.utils.parseEther(unstakeInput);
      const unstakeTx = await stakingContract.unstake(amount);
      await unstakeTx.wait();

      alert('Unstaked successfully!');
      setUnstakeInput('');
      await loadData(provider, account);
    } catch (error) {
      console.error('Error unstaking:', error);
      alert('Failed to unstake: ' + (error?.message || error));
    }
    setLoading(false);
  };

  const handleClaim = async () => {
    setLoading(true);
    try {
      const stakingContract = new ethers.Contract(
        STAKING_CONFIG.contractAddress,
        STAKING_ABI,
        signer
      );

      const claimTx = await stakingContract.claimRewards();
      await claimTx.wait();

      alert('Rewards claimed successfully!');
      await loadData(provider, account);
    } catch (error) {
      console.error('Error claiming:', error);
      alert('Failed to claim: ' + (error?.message || error));
    }
    setLoading(false);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(BESTCRYPTOTWITS_TOKEN.address);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="App">
      <header className="header">
        <h1>BESTswap</h1>
        {!account ? (
          <button onClick={connectWallet} className="connect-btn">
            Connect Wallet
          </button>
        ) : (
          <div className="account-info">
            {account.substring(0, 6)}...{account.substring(38)}
          </div>
        )}
      </header>

      {!account ? (
        <div className="welcome-container">
          <div className="welcome-card">
            <h2>Welcome to BESTswap</h2>
            <p>
              Connect your wallet to start staking BESTCRYPTOTWITS tokens and
              earn up to 700% APY
            </p>
            <button onClick={connectWallet} className="welcome-connect-btn">
              Connect Wallet to Use BESTswap
            </button>
          </div>
        </div>
      ) : (
        <div className="container">
          <div className="card">
            <h2>Your Balance</h2>
            <p className="balance">
              {parseFloat(tokenBalance).toFixed(2)} BESTCRYPTOTWITS
            </p>
          </div>

          <div className="card">
            <h2>Staking BESTCRYPTOTWITS:</h2>
            <p>- Claim once every 1hr</p>
            <p>- Min Stake period is 24hr</p>
            <p>- Max reward is 40x your original stake</p>
          </div>

          <div className="card">
            <h2>Stake Tokens</h2>
            <p>Earn 700% APY</p>
            <input
              type="number"
              value={stakeInput}
              onChange={(e) => setStakeInput(e.target.value)}
              placeholder="Amount to stake"
              className="input"
            />
            <button
              onClick={handleStake}
              disabled={loading}
              className="action-btn"
            >
              {loading ? 'Processing...' : 'Stake'}
            </button>
          </div>

          <div className="card">
            <h2>Your Staked Amount</h2>
            <p className="balance">
              {parseFloat(stakedAmount).toFixed(2)} BESTCRYPTOTWITS
            </p>
            <h3>Pending Rewards</h3>
            <p className="rewards">
              {parseFloat(pendingRewards).toFixed(6)} BESTCRYPTOTWITS
            </p>
            <button
              onClick={handleClaim}
              disabled={loading}
              className="action-btn"
            >
              Claim Rewards
            </button>
          </div>

          <div className="card">
            <h2>Unstake Tokens</h2>
            <input
              type="number"
              value={unstakeInput}
              onChange={(e) => setUnstakeInput(e.target.value)}
              placeholder="Amount to unstake"
              className="input"
            />
            <button
              onClick={handleUnstake}
              disabled={loading}
              className="action-btn"
            >
              {loading ? 'Processing...' : 'Unstake'}
            </button>
          </div>

          <div className="card">
            <h2>Buy BESTCRYPTOTWITS on Base:</h2>
            <p style={{marginBottom: '8px'}}>Contract address:</p>
            <div className="contract-address-container">
              <code className="contract-address">
                {BESTCRYPTOTWITS_TOKEN.address}
              </code>
              <button 
                onClick={copyToClipboard} 
                className="copy-btn"
                title="Copy to clipboard"
              >
                {copied ? '✓ Copied!' : 'Copy'}
              </button>
            </div>
          </div>
        </div>
      )}

      <a
        href="https://foxcoin-zeta.vercel.app/"
        target="_blank"
        rel="noopener noreferrer"
        className="foxcoin-btn"
      >
        FOXCOIN
      </a>
    </div>
  );
}

export default App;